import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { translate, TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AddEvent, CancelEvent, EditEvent, SaveEvent } from '@progress/kendo-angular-grid';
import { IBaseSearchRequest } from 'src/app/core/models/base';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { MatchValidator } from 'src/app/shared/validators/match-validator';
import { ICompany } from '../models/company';
import { EditUser, ICreateUserRequest, ISearchUserRquest, IUpdateUserRequest, IEmployee, Language} from '../models/employee';
import { CompanyService } from '../services/company.service';
import { EmployeeService } from '../services/employee.service';
import { IResetPasswordRequest, UserService } from '../services/user.service';
import { ConfirmationDialogService } from '../../shared/services/confirmation-dialog.service';
import { Subscription } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'app-company-employees',
    templateUrl: './company-employees.component.html',
    styleUrls: ['./company-employees.component.scss'],
    standalone: false
})
export class CompanyEmployeesComponent implements OnInit {

  @Input() company: ICompany;

  constructor(
    private route: ActivatedRoute,
    private employeeService : EmployeeService,
    private appnotificationService: AppNotificationService,
    private translocoService: TranslocoService,
    private companyService: CompanyService,
    private userService : UserService,
    private confirmationDialogService : ConfirmationDialogService
    ) { }

  employees: IEmployee[];

  currentUserId = null;

  public formGroup!: UntypedFormGroup;
  public editDataItem: EditUser;
  public isNew = false;

  get languages(){
    return Language
  }

  ngOnInit(): void {
    this.employees = this.route.snapshot.data['employees'] as IEmployee[];
  }

  request: IBaseSearchRequest = {
    pageNumber: 0,
    pageSize: 999999,
  };

  reloadCompany(){
    this.companyService.getCompany(this.company.id)
    .pipe(untilDestroyed(this))
    .subscribe(response => this.company = response)
  }

  // start grid editing

  public addHandler(args: AddEvent): void{
    this.isNew = true; 
    this.editDataItem = new EditUser();
  }

  public editHandler(args: EditEvent): void{
    this.isNew = false;
    this.editDataItem = args.dataItem;
  }

  public cancelHandler(): void {
    this.editDataItem = undefined;
  }

  public saveHandler(employee: EditUser): void{
    const createRequest: ICreateUserRequest = {
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      userName: employee.email,
      password: employee.password,
      language: employee.language,
      role: employee.role,
      companyId: this.company.id,
      phoneNumber:employee.phoneNumber,
    }
    const updateRequest: IUpdateUserRequest = {
      firstName: employee.firstName,
      lastName:employee.lastName,
      phoneNumber: employee.phoneNumber,
      language: employee.language,
      role: employee.role,
      id: this.editDataItem.id
    }
    if (!this.editDataItem.id){
      this.employeeService.createEmployee(createRequest)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.reloadCompany();
        this.appnotificationService.notification$.next({message:this.translocoService.translate('companies.employeeSafeSuccess'), style: "success"});
      })
    } 
    else {
      this.employeeService.updateEmployee(updateRequest)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.reloadCompany();
        this.appnotificationService.notification$.next({message:this.translocoService.translate('companies.employeeSafeSuccess'), style: "success"});
      })
    }

    this.editDataItem = undefined;
  }

  public removeHandler({dataItem}) {
    var subscription : Subscription = new Subscription();
    this.confirmationDialogService.open();
    subscription = this.confirmationDialogService.decision$.subscribe(val => {
      if (val) {
        this.employeeService.deleteEmployee(dataItem.id)
        .pipe(untilDestroyed(this))
          .subscribe(response => {
            this.reloadCompany();
            this.appnotificationService.notification$.next({message:this.translocoService.translate('companies.employeedeactivatedSuccess'), style: "success"});
          })
          this.confirmationDialogService.updateValue(false);
      } else{
        subscription.unsubscribe();
      }
    });
  }

  // end grid editing

  public opened = false;

  public close(status: string): void {
    this.opened = false;
    this.currentUserId = null;
    this.passwordForm.reset();
  }

  public open(currentEmployee): void {
    this.currentUserId = currentEmployee.userId;
    this.opened = true;
  }

  public passwordForm: UntypedFormGroup = new UntypedFormGroup({
    newPassword: new UntypedFormControl('', [Validators.required, Validators.minLength(6)]),
    confirmNewPassword: new UntypedFormControl('', Validators.required),
  }, [MatchValidator('newPassword','confirmNewPassword')])

  submitPasswordChange() {    
    if (this.passwordForm.invalid) {
      return;
    }

    const resetRequest: IResetPasswordRequest = {
      password : this.passwordForm.value.newPassword,
      userId : this.currentUserId
    }

    this.userService.resetPassword(resetRequest)
      .subscribe((): void => {
        this.appnotificationService.notification$.next({message: translate('profile.passwordUpdateSuccessful'),style:'success'})
        this.close('yes');
      });
  }

  get passwordMatchError() {
    return (
      this.passwordForm.getError('mismatch') &&
      this.passwordForm.get('confirmNewPassword')?.touched
    );
  }
}
